import { Checkbox } from '@/components/ui/checkbox';
/** Money renders server-owned amounts and keeps drawer actions on the same route.
 * b4d/REV2 supplies the current form, ledger and document layout authority.
 * Shared dashboard components retain their permission and recovery contracts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { moneyDeskContext } from '@/dashboard/patients/patient-master-view';
import {
  clinicOptionLabel,
  useClinicLens,
} from '@/dashboard/clinic-context';
import {
  attachMoneyEntryProofApi,
  downloadMoneyExport,
  fetchMoneyDay,
  fetchRecentPatientPayments,
  fetchMoneyEntryProofImage,
  fetchMoneyWorkspace,
  recordMoneyEntryApi,
  reverseMoneyEntryApi,
} from '@/dashboard/api';
import { PortalApiError } from '@/lib/portal-api';
import DrawerRecoveryNotice from './DrawerRecoveryNotice';
import FirstCountRecoveryNotice from './FirstCountRecoveryNotice';
import {pendingFirstCount,postFirstCount} from './first-count-recovery';
import {useConsultationIdentity} from '@/dashboard/billing/use-consultation-identity';
import {moneyOperationSession} from './money-operation-session';
import {pendingClose,requestCloseCheck,readCloseCheck,finishClose,changeSavedCount,type SavedClose,type CloseCheck} from './close-recovery';
import { parseInrToMinorUnits, formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';
import ReceiptPrintView from '@/dashboard/billing/ReceiptPrintView';
import type {PatientPaymentRow} from '@/dashboard/billing/billing-entry-view';
import AcBadge from '@/dashboard/ui/AcBadge';
import KpiTile from '@/dashboard/ui/KpiTile';
import AcButton from '@/dashboard/ui/AcButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import DataTable, { type DataTableColumn } from '@/dashboard/ui/DataTable';
import { useNarrowViewport } from '@/dashboard/coc/use-narrow-viewport';
import './MoneyDeskPage.css';
import {formatIstDate,formatTimestampIst} from '@/lib/datetime-ist';
import Calendar, {calendarDate,calendarDay} from '@/dashboard/ui/Calendar';
import {Popover} from '@base-ui/react/popover';
// b4d/REV2 uses the shared centered modal for forms and saved entry details.
import ModalShell from '@/dashboard/ui/ModalShell';
import EmptyState from '@/dashboard/ui/EmptyState';
import ErrorState from '@/dashboard/ui/ErrorState';
import LoadingState from '@/dashboard/ui/LoadingState';
import SectionShell from '@/dashboard/ui/SectionShell';
import SelectorBar, { type SelectorControlDef } from '@/dashboard/ui/SelectorBar';
import SummaryHeader from '@/dashboard/ui/SummaryHeader';
import ClinicScopeLabel from '@/dashboard/ui/ClinicScopeLabel';
import { SHELL_PAGE_CLASS } from '@/dashboard/ui/shell';
import {
  deriveCloseConclusion,
  deriveMoneyTodoItems,
  differenceReasonLabel,
  draftHasContent,
  draftStorageKey,
  formatRs,
  istDateLabel,
  istTimeLabel,
  parseDraft,
  serializeDraft,
  validateExpenseSheet,
  DAY_STATUS_LABELS,
  MONEY_NO_CHAIN_ASK_LINE,
  MONEY_NO_CHAIN_BODY,
  MONEY_NO_CHAIN_CAN_COUNT_LINE,
  MONEY_NO_CHAIN_TITLE,
  MONEY_TODO_EMPTY_TITLE,
  type MoneyDayWire,
  type MoneyDrawerEntryWire,
  type MoneySheetDraft,
} from '@/dashboard/money/money-desk-view';
import {
  composeSummaryTiles,
  tolerantRs,
  countLine,
  paymentModeLabel,
  presetForRange,
  proofTone,
  rangeIdentityLine,
  rangeLabel,
  resolveRangePreset,
  rowWhen,
  signedRs,
  validateDepositSheet,
  PROOF_LABELS,
  RANGE_PRESET_LABELS,
  VOID_STATE_LABELS,
  type MoneyCollectionRowWire,
  type MoneyDayLedgerRowWire,
  type MoneyDepositRowWire,
  type MoneyExpenseRowWire,
  type MoneyViewToken,
  type MoneyWorkspaceWire,
  type RangePreset,
  type ResolvedRange,
} from '@/dashboard/money/money-workspace-view';

// ---- Photo capture (DOM-bound; the pure fences live server-side and are
// mirrored client-side for the beside-the-field error) ----------------------

const CAPTURE_MAX_EDGE_PX = 1600;
const CAPTURE_JPEG_QUALITY = 0.85;

interface CapturedPhoto {
  readonly base64: string;
  readonly mimeType: string;
  readonly byteSize: number;
}

async function fileToCapturedPhoto(
  file: File,
  maxBytes: number,
  allowedTypes: readonly string[],
): Promise<CapturedPhoto> {
  // Downscale through a canvas (desk photos need legibility, not pixels);
  // fall back to the raw file when the canvas path is unavailable.
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the photo'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
  const viaCanvas: CapturedPhoto | null = await new Promise((resolve) => {
    const img = new Image();
    img.onerror = () => resolve(null);
    img.onload = () => {
      try {
        const scale = Math.min(
          1,
          CAPTURE_MAX_EDGE_PX / Math.max(img.width, img.height),
        );
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const jpeg = canvas.toDataURL('image/jpeg', CAPTURE_JPEG_QUALITY);
        const base64 = jpeg.slice(jpeg.indexOf(',') + 1);
        resolve({
          base64,
          mimeType: 'image/jpeg',
          byteSize: Math.floor((base64.length * 3) / 4),
        });
      } catch {
        resolve(null);
      }
    };
    img.src = dataUrl;
  });
  const captured =
    viaCanvas ??
    (() => {
      const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
      return {
        base64,
        mimeType: file.type,
        byteSize: Math.floor((base64.length * 3) / 4),
      };
    })();
  if (!allowedTypes.includes(captured.mimeType)) {
    throw new Error('Photo type not accepted. Use a JPEG, PNG, or WebP photo.');
  }
  if (captured.byteSize > maxBytes) {
    throw new Error(
      `Photo is too large. Max ${Math.floor(maxBytes / (1024 * 1024))} MB.`,
    );
  }
  return captured;
}

function apiErrorMessage(err: unknown): string {
  if (err instanceof PortalApiError) return err.message;
  return err instanceof Error ? err.message : 'Something went wrong';
}

// CP1 presentation only: never render transport/server diagnostics to the desk.
// Recovery and request identity stay in the existing operation layer.
function drawerFeedback(err: unknown, action: 'save' | 'voucher' | 'read' | 'reverse' | 'deposit' | 'close'): string {
  if (err instanceof PortalApiError) {
    if (err.status === 401) return 'Your session has expired. Sign in again before continuing.';
    if (err.status === 403) return 'You do not have permission for this action in the selected clinic.';
    if (err.status === 404) return 'This entry is unavailable in the selected clinic. Reload the Money page.';
    const known: Record<string, string> = {
      source_changed: 'The day changed. Count again and request a new check.',
      close_check_changed: 'This count no longer matches its second check. Request a new check.',
      day_already_closed: 'This day is closed. Reload the ledger before making a correction.',
      entry_already_reversed: 'This entry already has a reversal. Reload the ledger to see it.',
      proof_already_attached: 'This entry already has a voucher. Reload it to view the saved image.',
      drawer_request_conflict: 'This request does not match the saved action. Recover that action before making another change.',
      drawer_unavailable: 'Saving is temporarily unavailable. Keep this window open and try again.',
      VALIDATION_FAILED: 'Some entry details could not be accepted. Check the fields before trying again.',
    };
    if (err.code && known[err.code]) return known[err.code]!;
  }
  // Only these local, user-facing recovery messages may pass through unchanged.
  const local = err instanceof Error ? err.message : '';
  const recoveryMessages = [
    'Sign in before continuing.', 'Your session identity is unavailable.',
    'Your session changed. Reopen the saved operation.', 'Your money scope is unavailable.',
    'An unconfirmed operation belongs to another scope. Restore that scope to resolve it.',
    'Your session changed. Reopen the saved operation in its original session.',
    'The saved drawer action could not be read. Keep this session for recovery.',
    'Recover the saved drawer action before making another change.',
    'Recovery could not be saved. Nothing was sent.',
    'The drawer action could not be confirmed. Recover the saved request.',
    'Open the original branch to recover this action.',
    'Could not read the photo', 'Photo type not accepted. Use a JPEG, PNG, or WebP photo.',
  ];
  if (!(err instanceof PortalApiError) && recoveryMessages.includes(local)) return local;
  if (!(err instanceof PortalApiError) && /^Photo is too large\. Max \d+ MB\.$/.test(local)) return local;
  if (action === 'read') return 'The voucher could not be loaded. Try loading it again.';
  if (action === 'reverse') return 'Reversal not confirmed. Keep this window open and retry the same reversal.';
  if (action === 'voucher') return 'Voucher save not confirmed. Keep this window open and retry.';
  if (action === 'deposit') return 'Deposit save not confirmed. Retry the same saved request in the original staff account and clinic.';
  if (action === 'close') return 'Close not confirmed. Check the same saved count in the original staff account and clinic.';
  return 'Expense save not confirmed. Your entered values are kept. Retry the same save to confirm the outcome.';
}

// ---- THE ONE MONEY WORKSPACE (A1) ------------------------------------------
//
// Three zones, top to bottom, and NOTHING else on the route:
//   1) SUMMARY BAND    -- labeled numbers for the selected scope (A2/R4)
//   2) VIEW CONTROLS   -- view picklist | date range | per-view filter |
//                         role-gated Export | the lawful inline actions
//                         (R2/R3/R6/R7)
//   3) TABLE REGION    -- ONE dense enterprise table rendering the view
//
// A5 NO-TOGGLING BOUNDARY: add expense, record deposit, view a proof
// image, first count and close all happen IN PLACE. Nothing on this route
// navigates away to do a core money-day action.

type OpenPanel =
  | { readonly kind: 'none' }
  | { readonly kind: 'expense'; readonly initialKind?: 'spent' | 'petty_cash' }
  | { readonly kind: 'deposit' }
  | { readonly kind: 'close' }
  | { readonly kind: 'first_count' }
  | { readonly kind: 'entry_details'; readonly entry: MoneyDrawerEntryWire };

export default function MoneyDeskPage(): JSX.Element {
  const clinicId = useClinicLens()?.selectedClinicId ?? null;
  const identity = useConsultationIdentity();
  // Reuse the billing identity boundary; no old branch data or open money
  // sheet survives a change of actor, role, organization or clinic.
  return <MoneyDeskWorkspace key={JSON.stringify([identity, clinicId])} />;
}

function MoneyDeskWorkspace(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.toString();
  const context = useMemo(() => moneyDeskContext(searchParams), [query]);
  // Keep sequential control changes in one event based on the latest values,
  // while an incoming same-route navigation replaces them immediately.
  const pendingContext = useRef(context);
  const lastLocationQuery = useRef(query);
  if (lastLocationQuery.current !== query) {
    pendingContext.current = context;
    lastLocationQuery.current = query;
  }
  const updateContext = (change: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(pendingContext.current);
    change(next);
    const safe = moneyDeskContext(next);
    pendingContext.current = safe;
    if (safe.toString() !== query) setSearchParams(safe, { replace: true });
  };

  // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1, Phase 2;
  // founder ruling 4d names this surface explicitly). Money desk is
  // OPERATIONAL -- money CAPTURE at the clinic you are working in, never
  // branch comparison -- so it reads the lens and keeps no private clinic.
  //
  // It previously read no lens at all: it rebuilt its own option list and
  // defaulted to the LOWEST CLINIC ID, so an owner opened it on an arbitrary
  // branch with nothing on the page naming that branch. Its own "Branch"
  // control is gone too: a second switcher for a solved interaction is a
  // defect, and the header lens is the switcher.
  const clinicCtx = useClinicLens();
  const clinicId = clinicCtx?.selectedClinicId ?? null;

  // R3: the workspace opens on TODAY. `null` means "server, use today" --
  // the client never invents a date, so a browser in another timezone can
  // never ask for the wrong day.
  const from = context.get('from');
  const to = context.get('to');
  const range = useMemo<ResolvedRange | null>(() => {
    return from !== null && to !== null ? { from, to } : null;
  }, [from, to]);
  const view = context.get('view') as MoneyViewToken | null;
  const filter = context.get('filter') ?? 'all';
  const setRange = (value: ResolvedRange | null) => updateContext(next => {
    if (value === null) { next.delete('from'); next.delete('to'); }
    else { next.set('from', value.from); next.set('to', value.to); }
  });
  const setView = (value: MoneyViewToken | null) => updateContext(next => {
    if (value === null) { next.delete('view'); next.delete('filter'); }
    else next.set('view', value);
  });
  const setFilter = (value: string) => updateContext(next => {
    if (value === 'all') next.delete('filter');
    else {
      if (!next.has('view')) next.set('view', ws?.view ?? 'day_ledger');
      next.set('filter', value);
    }
  });
  const [rangeOpen,setRangeOpen]=useState(false);
  const [rangeStart,setRangeStart]=useState<string|null>(null);
  const [rangeError,setRangeError]=useState<string|null>(null);
  const [rangeMonth,setRangeMonth]=useState<Date|undefined>(undefined);
  const [customRange,setCustomRange]=useState(false);
  const [cashRowsFilter,setCashRowsFilter]=useState<'opening'|'cash_in'|'counted_difference'|null>(null);

  const [ws, setWs] = useState<MoneyWorkspaceWire | null>(null);
  const [day, setDay] = useState<MoneyDayWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [panel, updatePanel] = useState<OpenPanel>({ kind: 'none' });
  const panelOpener = useRef<HTMLElement | null>(null);
  const setPanel = (next: OpenPanel) => {
    if (panel.kind === 'none' && next.kind !== 'none') panelOpener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (next.kind === 'none') {
      const active = document.activeElement;
      const shouldReturn = active === document.body || (active instanceof HTMLElement && active.closest('[role="dialog"]') !== null);
      if (shouldReturn) queueMicrotask(() => { if (panelOpener.current?.isConnected) panelOpener.current.focus(); });
    }
    updatePanel(next);
  };
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [receipt,setReceipt] = useState<PatientPaymentRow|null>(null);
  const [receiptDocumentTitle,setReceiptDocumentTitle]=useState('Payment receipt');
  const [receiptBusy,setReceiptBusy] = useState(false);
  const [receiptError,setReceiptError] = useState<string|null>(null);

  const mounted = useRef(false);
  const readGeneration = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; readGeneration.current++; };
  }, []);

  const openReceipt=async(paymentId:number)=>{
    if(clinicId===null||receiptBusy)return;
    setReceiptBusy(true);setReceiptError(null);
    try{const result=await fetchRecentPatientPayments(clinicId,paymentId);
      if(!mounted.current)return;
      if(result.clinicId!==clinicId||result.scope?.clinicId!==clinicId||result.scope.paymentId!==paymentId||result.scope.patientId!==null)throw new Error('Receipt scope could not be confirmed.');
      const payment=result.payments.find(row=>row.id===paymentId&&row.clinicId===clinicId);
      if(!payment)throw new Error('Receipt unavailable in this clinic.');setReceipt(payment);
    }catch(error){if(mounted.current)setReceiptError(apiErrorMessage(error));}
    finally{if(mounted.current)setReceiptBusy(false);}
  };

  const refetch = useCallback(async () => {
    if (clinicId === null || !mounted.current) return;
    const generation = ++readGeneration.current;
    const isCurrent = () => mounted.current && generation === readGeneration.current;
    setRefreshing(true);
    try {
      const workspace = await fetchMoneyWorkspace({
        clinicId,
        ...(range !== null ? { from: range.from, to: range.to } : {}),
        ...(view !== null ? { view } : {}),
        filter,
      });
      if (!isCurrent()) return;
      // The DAY read backs the in-place ACTIONS (sheet config, chain state,
      // the close recap). Its date is the range's own day when the operator
      // is looking at one day, and today otherwise -- an action always has
      // an unambiguous day, and it is never a day the operator cannot see.
      const actionDate =
        workspace.range.from === workspace.range.to
          ? workspace.range.from
          : workspace.range.today;
      const dayWire = await fetchMoneyDay(clinicId, actionDate);
      if (!isCurrent()) return;
      setWs(workspace);
      setDay(dayWire);
      setLoadError(null);
    } catch (err) {
      if (isCurrent()) setLoadError(apiErrorMessage(err));
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [clinicId, range, view, filter]);

  useEffect(() => {
    void refetch();
    return () => { readGeneration.current++; };
  }, [refetch]);

  const onExport = useCallback(async () => {
    if (clinicId === null || ws === null || !mounted.current) return;
    setExporting(true);
    setExportError(null);
    try {
      const { blob, filename } = await downloadMoneyExport({
        clinicId,
        from: ws.range.from,
        to: ws.range.to,
        view: ws.view,
        filter: ws.filter,
      });
      if (!mounted.current) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (mounted.current) setExportError(apiErrorMessage(err));
    } finally {
      if (mounted.current) setExporting(false);
    }
  }, [clinicId, ws]);

  if (loading && ws === null) {
    return (
      <div className={SHELL_PAGE_CLASS} data-testid="money-desk-loading">
        <LoadingState label="Loading the money workspace..." lines={3} />
      </div>
    );
  }
  if (ws === null || day === null) {
    return (
      <div className={SHELL_PAGE_CLASS} data-testid="money-desk-error">
        <ErrorState
          title="Could not load the money workspace"
          detail={loadError}
          action={
            <AcButton
              variant="secondary"
              onClick={() => {
                setLoading(true);
                void refetch();
              }}
            >
              Try again
            </AcButton>
          }
        />
      </div>
    );
  }

  // The clinic name comes from THE ONE labeller -- never an id.
  const clinicOption =
    clinicCtx?.options.find((c) => c.clinicId === clinicId) ?? null;
  const clinicLabel =
    clinicOption !== null ? clinicOptionLabel(clinicOption) : '';
  const activeRange: ResolvedRange = { from: ws.range.from, to: ws.range.to };
  const activeView =
    ws.views.find((v) => v.token === ws.view) ?? ws.views[0] ?? null;
  const activeFilterLabel =
    activeView?.filters.find((f) => f.token === ws.filter)?.label ?? ws.filter;
  const closed = day.closedDay;
  const isSingleDay = ws.range.from === ws.range.to;
  // Writes belong to the day the operator is looking at, and only when
  // that day is open. A multi-day reconstruction is READ-ONLY (D1): there
  // is no unambiguous day to write to, so no write control renders.
  const canActOnDay =
    day.viewer.canWrite && isSingleDay && day.dayStatus !== 'acknowledged';
  const canClose =
    canActOnDay &&
    day.dayStatus !== 'no_chain' &&
    day.dayStatus !== 'pending_prior_day';

  const setPreset = (preset: RangePreset) => {
    setRangeError(null);setRangeStart(null);setCustomRange(preset==='custom');
    if (preset === 'custom') return;
    setRange(resolveRangePreset(preset, ws.range.today));setRangeOpen(false);
  };

  const cashRows = ws.view==='day_ledger' ? (ws.rows as readonly MoneyDayLedgerRowWire[]).filter(row=>cashRowsFilter==='opening'?row.typeToken==='day_start':cashRowsFilter==='counted_difference'?row.typeToken==='day_close':row.typeToken==='collection'&&row.mode==='cash') : [];

  // ---- Zone 1: SUMMARY BAND (A2/R4) ----
  const tiles = composeSummaryTiles({
    summary: ws.summary,
    range: ws.range,
    chainState: ws.chain.state,
  });
  const identityLine = !ws.summary.collectionsCapped && !ws.summary.entriesCapped &&
    (ws.summary.postCloseAdjustmentStatus==='not_closed'||(ws.summary.postCloseAdjustmentStatus==='measured'&&Number.isSafeInteger(ws.summary.postCloseAdjustmentMinorUnits))) ? rangeIdentityLine(ws.summary) : null;
  // F4: the rail's DISPLAY list, from the SAME wire the actions read.
  const todoItems = deriveMoneyTodoItems(day);
  const viewControls: readonly SelectorControlDef[] = [
    {
      key: 'view',
      label: 'View',
      kind: 'saved' as const,
      value: ws.view,
      options: ws.views.map((v) => ({ value: v.token, label: v.token === 'day_ledger' ? 'Ledger' : v.label })),
      onChange: (v: string) => {
        setView(v as MoneyViewToken);setCashRowsFilter(null);
        // R7: a filter belongs to its view; switching views resets to
        // the honest default rather than carrying a foreign token.
        setFilter('all');
      },
    },

  ];

  return (
    <div className={`${SHELL_PAGE_CLASS} money-workspace`} data-testid="money-desk">
      {receiptError?<p role="alert">{receiptError}</p>:null}
      {receipt?<ModalShell size="lg" trapFocus childHeaderActions testId="saved-receipt-panel" title={receiptDocumentTitle} onClose={()=>setReceipt(null)}><ReceiptPrintView onDocumentTitleChange={setReceiptDocumentTitle} payment={receipt} clinicLabel={clinicLabel} backLabel="Back to Money" onBack={()=>setReceipt(null)}/></ModalShell>:null}
      <FirstCountRecoveryNotice clinicId={clinicId} onRecovered={()=>{setPanel({kind:'none'});void refetch();}} />
      <DrawerRecoveryNotice clinicId={clinicId} onRecovered={()=>{setPanel({kind:'none'});void refetch();}} />
      <SummaryHeader
        title="Money"
        scope={
          clinicLabel !== '' ? (
            <ClinicScopeLabel label={clinicLabel} testId="money-clinic-scope" />
          ) : null
        }
        subs={[
          <span key="range" data-testid="money-range-label">
            {rangeLabel(ws.range)} - IST
          </span>,
          <span key="status" data-testid="money-day-status">
            {DAY_STATUS_LABELS[day.dayStatus]}
          </span>,
        ]}
        action={<>
          {canActOnDay?<AcButton variant="ghost" data-testid="open-petty-cash" onClick={()=>setPanel({kind:'expense',initialKind:'petty_cash'})}>{day.config.pettyCashLabel}</AcButton>:null}
          {canActOnDay ? <Button className="money-workspace-button money-workspace-button--quiet" onClick={()=>setPanel({kind:'deposit'})} data-testid="open-record-deposit">Record deposit</Button> : null}
          {canClose ? <Button className="money-workspace-button money-workspace-button--quiet" onClick={()=>setPanel({kind:'close'})} data-testid="open-close-day">Count and close</Button> : null}
          {canActOnDay ? <Button className="money-workspace-button" onClick={()=>setPanel({kind:'expense'})} data-testid="open-add-expense">Add expense</Button> : null}
        </>}
        tiles={[]}
      />

      {/* ---- Zone 2: VIEW CONTROLS (R2/R3/R6/R7) ---- */}
      <div className="money-range-controls" data-testid="money-view-controls">
        <div className="money-date-control"><span id="money-date-label">Date</span><Popover.Root open={rangeOpen} onOpenChange={open=>{setRangeOpen(open);if(open){setRangeStart(null);setRangeError(null);setCustomRange(false);setRangeMonth(calendarDate((range??activeRange).from,'Asia/Kolkata'));}}}>
          <Popover.Trigger className="ac-btn ac-btn--secondary" aria-label="Money date range">{formatIstDate(`${activeRange.from}T00:00:00+05:30`)}{activeRange.from!==activeRange.to?` – ${formatIstDate(`${activeRange.to}T00:00:00+05:30`)}`:''}</Popover.Trigger>
          <Popover.Portal><Popover.Positioner sideOffset={6} align="start" className="money-range-positioner"><Popover.Popup className="money-range-popover">
            <label htmlFor="money-range-preset">Date range</label><select id="money-range-preset" value={customRange||rangeStart?'custom':presetForRange(activeRange,ws.range.today)} onChange={event=>setPreset(event.target.value as RangePreset)}>
              {(['today','yesterday','last_7','this_month','custom'] as const).map(value=><option key={value} value={value}>{RANGE_PRESET_LABELS[value]}</option>)}
            </select>
            <Calendar components={{Chevron: ({orientation}) => <span aria-hidden="true">{orientation === 'left' ? '‹' : '›'}</span>}} mode="range" navLayout="around" required timeZone="Asia/Kolkata" month={rangeMonth} onMonthChange={setRangeMonth} today={calendarDate(ws.range.today,'Asia/Kolkata')} endMonth={calendarDate(ws.range.today,'Asia/Kolkata')} disabled={{after:calendarDate(ws.range.today,'Asia/Kolkata')}} showOutsideDays={false}
              selected={rangeStart?{from:calendarDate(rangeStart,'Asia/Kolkata'),to:undefined}:{from:calendarDate(activeRange.from,'Asia/Kolkata'),to:calendarDate(activeRange.to,'Asia/Kolkata')}}
              onSelect={(_selected,triggerDate)=>{
                const chosen=calendarDay(triggerDate,'Asia/Kolkata');setRangeError(null);
                if(!rangeStart){setRangeStart(chosen);setCustomRange(true);return;}
                const from=chosen<rangeStart?chosen:rangeStart;const to=chosen<rangeStart?rangeStart:chosen;
                if((Date.parse(to+'T00:00:00Z')-Date.parse(from+'T00:00:00Z'))/86400000+1>92){setRangeError('Choose up to 92 days.');return;}
                setRange({from,to});setRangeStart(null);setRangeOpen(false);
              }}/>
            <small role="status">{rangeError??(rangeStart?`Start ${istDateLabel(rangeStart)} · choose the end date`:'Choose the first and last date in one calendar.')}</small>
          </Popover.Popup></Popover.Positioner></Popover.Portal>
        </Popover.Root></div>
        <SelectorBar controls={viewControls}/>
        {activeRange.from!==ws.range.today||activeRange.to!==ws.range.today?<AcButton variant="ghost" onClick={()=>setPreset('today')}>Return to current day</AcButton>:null}
        {/* R6/A4: the export control renders ONLY for a role holding the
            configured privilege. The SERVER refuses the endpoint for every
            other role -- this absence is courtesy, never the fence. */}
        {ws.viewer.canExport ? (
          <AcButton
            variant="secondary"
            size="sm"
            onClick={() => void onExport()}
            disabled={exporting}
            data-testid="money-export"
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </AcButton>
        ) : null}
      </div>
      <section className={`money-cash-band${isSingleDay&&closed ? ' money-cash-band--closed' : ''}`} aria-label="Cash summary" data-testid="money-summary-band">
        {[
          {key:'opening',label:'Day start',value:ws.summary.openingMinorUnits,view:'day_ledger'},
          {key:'cash_in',label:'+ Cash in',value:Array.isArray(ws.summary.collectedByMode)?(ws.summary.collectedByMode.find(mode=>mode.method==='cash')?.totalMinorUnits??0):null,view:'day_ledger'},
          {key:'expenses',label:'− Spent',value:ws.summary.expensesMinorUnits,view:'expenses'},
          {key:'deposits',label:'− Deposited',value:ws.summary.depositsMinorUnits,view:'deposits'},
          {key:'cash_in_hand',label:!isSingleDay?'Cash at range end':closed?'Cash at close':'= Cash in drawer',value:isSingleDay&&closed?closed.countedCloseMinorUnits:ws.summary.cashInHandMinorUnits,view:'day_ledger'},
          ...(isSingleDay&&closed?[{key:'counted_difference',label:'Counted difference',value:closed.differenceMinorUnits,view:'day_ledger'}]:[]),
        ].map((tile)=><KpiTile key={tile.key} label={tile.label} value={tile.key==='counted_difference'&&tile.value!==null?`${tile.value<0?'− ':tile.value>0?'+ ':''}${formatMinorUnitsInr(Math.abs(tile.value),2)}`:typeof tile.value==='number'&&Number.isSafeInteger(tile.value)?formatMinorUnitsInr(tile.value,2):tolerantRs(tile.value)} size="base"
          testId={`money-tile-${tile.key}`} sub={tile.value===null?'Unavailable':undefined}
          onClick={ws.views.some(view=>view.token===tile.view)?()=>{setView(tile.view as MoneyViewToken);setFilter('all');setCashRowsFilter(tile.key==='opening'?'opening':tile.key==='cash_in'?'cash_in':tile.key==='counted_difference'?'counted_difference':null);document.querySelector('[data-testid="money-table-region"]')?.scrollIntoView({block:'nearest'});}:undefined}/>) }

      </section>


      {exportError !== null ? (
        <ErrorState title="Export failed" detail={exportError} />
      ) : null}

      {/* ---- Chain + close state (honest, in place) ---- */}
      {ws.chain.state === 'no_chain' ? (
        // E1: the guided first-open action, IN the workspace. The bare
        // "No first count" state the walk found (M7) is replaced by what
        // an opening count IS plus one primary action for the lawful role;
        // anyone else is told who to ask (never a bodyless dead end).
        <SectionShell title="Opening count">
          <EmptyState
            title={MONEY_NO_CHAIN_TITLE}
            body={
              <>
                {MONEY_NO_CHAIN_BODY}{' '}
                <span data-testid="first-count-actor-line">
                  {day.viewer.canFirstCount
                    ? MONEY_NO_CHAIN_CAN_COUNT_LINE
                    : MONEY_NO_CHAIN_ASK_LINE}
                </span>
              </>
            }
            action={
              day.viewer.canFirstCount ? (
                <AcButton
                  variant="primary"
                  onClick={() => setPanel({ kind: 'first_count' })}
                  data-testid="open-first-count"
                >
                  Record the first count
                </AcButton>
              ) : undefined
            }
            testId="money-no-chain"
          />
        </SectionShell>
      ) : null}

      {ws.chain.state === 'pending_prior_day' ? (
        <SectionShell title="Earlier day open">
          <EmptyState
            title={`Close ${istDateLabel(ws.chain.pendingDates[0] ?? '')} first`}
            body="Days close in order. Balances stay undecided until that day is acknowledged - nothing is invented."
            action={
              canClose ? (
                <AcButton
                  variant="secondary"
                  onClick={() =>
                    setRange({
                      from: ws.chain.pendingDates[0] ?? ws.range.from,
                      to: ws.chain.pendingDates[0] ?? ws.range.to,
                    })
                  }
                  data-testid="goto-pending-day"
                >
                  Go to that day
                </AcButton>
              ) : undefined
            }
            testId="pending-prior-strip"
          />
        </SectionShell>
      ) : null}

      {isSingleDay && closed !== null ? (
        <details className="money-closed-recap" data-testid="closed-recap">
          <summary>Day closed · {closed.differenceMinorUnits===0?'Count matches':`${closed.differenceMinorUnits<0?'− ': '+ '}${formatMinorUnitsInr(Math.abs(closed.differenceMinorUnits),2)} difference`}</summary>
          <p>
            Closed at {formatRs(closed.countedCloseMinorUnits)} - carried
            forward to the next day. Acknowledged by{' '}
            {closed.acknowledgedByLabel} at {istTimeLabel(closed.acknowledgedAt)}
            {closed.verifiedByDoctorName
              ? `, verified by ${closed.verifiedByDoctorName}`
              : ''}
            . Fixes post forward as reversing entries.
          </p>
          {closed.differenceMinorUnits !== 0 ? (
            <p data-testid="closed-difference">
              <AcBadge tone="pending">
                {formatRs(Math.abs(closed.differenceMinorUnits))}{' '}
                {closed.differenceMinorUnits < 0 ? 'short' : 'over'}
              </AcBadge>{' '}
              {closed.differenceReasonCategory
                ? differenceReasonLabel(closed.differenceReasonCategory)
                : ''}
              {closed.differenceReasonText
                ? ` - ${closed.differenceReasonText}`
                : ''}
            </p>
          ) : null}
        </details>
      ) : null}

      {ws.summary.collectionsCapped || ws.summary.entriesCapped ? (
        <SectionShell title="Large range">
          <p data-testid="capped-note">
            This range hit its scan bound, so the figures above are a labeled
            LOWER BOUND. Narrow the dates for exact totals.
          </p>
        </SectionShell>
      ) : null}

      {ws.days.some(value=>value.acknowledged&&(value.postCloseStatus!=='measured'||value.postCloseAdjustmentMinorUnits!==0))?<SectionShell title="Later collection correction" testId="later-collection-correction">
        {ws.days.filter(value=>value.acknowledged&&(value.postCloseStatus!=='measured'||value.postCloseAdjustmentMinorUnits!==0)).map(value=><p key={value.businessDate}>{istDateLabel(value.businessDate)} · {value.postCloseStatus==='measured'&&typeof value.postCloseAdjustmentMinorUnits==='number'&&Number.isSafeInteger(value.postCloseAdjustmentMinorUnits)?signedRs(value.postCloseAdjustmentMinorUnits):'Unavailable — the source or carry chain could not be compared'}. Counted close {tolerantRs(value.countedCloseMinorUnits)} remains unchanged. This is an explanation of later collection changes, not a new cash movement.</p>)}
      </SectionShell>:null}
      <div className="money-workspace-layout">
      {/* ---- Zone 3: TABLE REGION (A1.3) ----
           ONE dense enterprise table, the REGISTERED DataTable primitive.
           No nested cards, no card wall, no Money-local table grammar. */}
      <SectionShell
        title={activeView?.token === 'day_ledger' ? 'Ledger' : activeView?.label ?? 'Rows'}
        testId="money-table-region"
        sub={<span data-testid="money-row-count">{countLine({rowCount:cashRowsFilter?cashRows.length:ws.rowCount,dayCount:ws.range.dayCount,filter:ws.filter,filterLabel:activeFilterLabel,capped:ws.summary.collectionsCapped||ws.summary.entriesCapped})}</span>}
        actions={cashRowsFilter||ws.filter!=='all'?<AcButton variant="ghost" onClick={()=>{setCashRowsFilter(null);setFilter('all');}}>All rows</AcButton>:undefined}
      >
        {activeView && activeView.filters.length > 1 ? <div className="money-ledger-filters" role="group" aria-label="Ledger filters">
          {activeView.filters.map(item => <AcButton key={item.token} variant={ws.filter===item.token&&!cashRowsFilter?'primary':'secondary'} aria-pressed={ws.filter===item.token&&!cashRowsFilter} onClick={()=>{setCashRowsFilter(null);setFilter(item.token);}}>
            {item.label} <span aria-label={item.count==null?'Count unavailable':`${item.count} rows`}>{item.count??'—'}</span>
          </AcButton>)}
        </div>:null}
        <div className={ws.view === 'day_ledger' ? 'money-ledger money-ledger--day' : 'money-ledger'}>
        <MoneyViewTable
          ws={cashRowsFilter&&ws.view==='day_ledger'?{...ws,rows:cashRows,rowCount:cashRows.length}:ws}
          refreshing={refreshing}
          filterLabel={activeFilterLabel}
          onOpenReceipt={id=>void openReceipt(id)} receiptBusy={receiptBusy}
          onOpenEntry={(entryId) => {
            const entry = day.drawerEntries.find((e) => e.id === entryId);
            if (entry) setPanel({ kind: 'entry_details', entry });
            else setReceiptError('Entry details are unavailable in this day. Choose its date to open the saved entry.');
          }}
        />
        </div>
      </SectionShell>

      {/* ---- FO-7 + F4: the TO-DO rail, preserved through the rebuild ----
           The owed duties for the day the operator is acting on, derived by
           THE EXISTING deriveMoneyTodoItems off the SAME day wire (no second
           derivation). Kept because it is the only surface that carries the
           MISSING-PROOF and DEPOSIT-DUE duties as ACTIONS -- the views show
           those rows, but a row is not a duty. F4's own law holds: the rail
           derives the owed first count from dayStatus, so "Nothing waiting"
           can never render beside a state that names a blocker. */}
      {isSingleDay ? (
        <SectionShell testId="money-todo-rail" title="To do" sub={todoItems.length === 0 && !canClose ? <span data-testid="todo-empty">{MONEY_TODO_EMPTY_TITLE}</span> : undefined}>
          {todoItems.length === 0 ? null : (
            <ul className="ac-tasklist">
              {todoItems.map((item, i) => (
                <li
                  className="ac-tasklist__row"
                  key={`${item.kind}-${i}`}
                  data-testid={`todo-${item.kind}`}
                >
                  {item.kind === 'missing_proof' ? <span className="money-voucher-duty">Add voucher<small>{day.drawerEntries.find(e => e.id === item.task?.entryId)?.particulars}</small></span> : (
                  <AcBadge
                    tone={
                      item.tone === 'warn'
                        ? 'pending'
                        : item.tone === 'info'
                          ? 'info'
                          : 'neutral'
                    }
                  >
                    {item.label}
                  </AcBadge>
                  )}
                  {item.kind === 'first_count' && day.viewer.canFirstCount ? (
                    <AcButton
                      variant="secondary"
                      size="sm"
                      onClick={() => setPanel({ kind: 'first_count' })}
                      data-testid="todo-first-count-action"
                    >
                      First count
                    </AcButton>
                  ) : null}
                  {item.task !== null &&
                  item.task.kind === 'missing_proof' &&
                  item.task.entryId !== null ? (
                    <Button
                      className="money-workspace-button money-workspace-button--quiet"
                      onClick={() => {
                        const entry = day.drawerEntries.find(
                          (e) => e.id === item.task!.entryId,
                        );
                        if (entry) setPanel({ kind: 'entry_details', entry });
                      }}
                      data-testid="todo-missing-proof-action"
                    >
                      Add photo
                    </Button>
                  ) : null}
                  {item.task !== null &&
                  item.task.kind === 'deposit_due' &&
                  canActOnDay ? (
                    <AcButton
                      variant="secondary"
                      size="sm"
                      onClick={() => setPanel({ kind: 'deposit' })}
                      data-testid="todo-deposit-action"
                    >
                      Record deposit
                    </AcButton>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {canClose?<div className="money-count-duty"><span>Count the drawer<small>When ready to close</small></span><AcButton variant="ghost" onClick={()=>setPanel({kind:'close'})}>Count cash</AcButton></div>:null}
        </SectionShell>
      ) : null}


        <Card className="money-collected-card" data-testid="money-collected-rail"><CardContent className="money-collected-content">
          <h2>Collected · all methods</h2>
          <strong data-testid="recorded-collections">{typeof ws.summary.collectedMinorUnits==='number'&&Number.isSafeInteger(ws.summary.collectedMinorUnits)?formatMinorUnitsInr(ws.summary.collectedMinorUnits,2):tolerantRs(ws.summary.collectedMinorUnits)}</strong>
          <p>Voided payments excluded</p>
          {ws.views.some(view=>view.token==='collections_by_mode')?<AcButton variant="ghost" onClick={()=>{setView('collections_by_mode');setFilter('all');}}>Collection rows</AcButton>:null}
          {day.viewer.canWrite?<a className="money-collection-link" href="/walkin">Collect from a patient</a>:null}
        </CardContent></Card>
      </div>

      <details className="money-cash-calculation"><summary>Cash calculation</summary>
        <p>{identityLine ?? 'Calculation unavailable until the opening and source records are resolved.'}</p>
        {tiles.map(tile=><p key={tile.key}><strong>{tile.label}</strong> · {tile.value}. {tile.sub}</p>)}
        <p>Recorded payments, expenses, deposits, reversals and counted differences retain their own meaning. An unavailable value is never zero.</p>
      </details>

      {/* ---- Panels: every core action, IN PLACE (A5/R5) ---- */}
      {panel.kind === 'expense' && clinicId !== null ? (
        <ExpenseSheet
          clinicLabel={clinicLabel}
          initialKind={panel.initialKind}
          day={day}
          currentBusinessDate={ws.range.today}
          clinicId={clinicId}
          onClose={() => setPanel({ kind: 'none' })}
          onSaved={() => void refetch()}
        />
      ) : null}

      {panel.kind === 'deposit' && clinicId !== null ? (
        <DepositSheet
          clinicLabel={clinicLabel}
          day={day}
          currentBusinessDate={ws.range.today}
          clinicId={clinicId}
          onClose={() => setPanel({ kind: 'none' })}
          onSaved={() => void refetch()}
        />
      ) : null}

      {panel.kind === 'close' && clinicId !== null ? (
        <CloseDaySheet
          key={JSON.stringify([clinicId, day.businessDate])}
          ledgerRows={ws.view==='day_ledger'&&ws.filter==='all' ? ws.rows as readonly MoneyDayLedgerRowWire[] : null}
          onRequestRows={()=>{setView('day_ledger');setFilter('all');}}
          day={day}
          clinicId={clinicId}
          onClose={() => setPanel({ kind: 'none' })}
          onClosed={() => {
            setPanel({ kind: 'none' });
            void refetch();
          }}
        />
      ) : null}

      {panel.kind === 'first_count' && clinicId !== null ? (
        <FirstCountSheet
          clinicId={clinicId}
          onClose={() => setPanel({ kind: 'none' })}
          onSaved={() => {
            setPanel({ kind: 'none' });
            void refetch();
          }}
        />
      ) : null}

      {panel.kind === 'entry_details' && clinicId !== null ? (
        <EntryDetailsDrawer
          entry={panel.entry}
          day={day}
          currentBusinessDate={ws.range.today}
          clinicId={clinicId}
          onClose={() => setPanel({ kind: 'none' })}
          onChanged={() => void refetch()}
        />
      ) : null}
    </div>
  );
}

// ---- The ONE table region (R2: the view picklist drives THIS) --------------

/** Short time only when the surrounding range explicitly names this one day. */
export function moneyRowWhen(businessDate: string, atIso: string | null, from: string, to: string): string {
  return from === to && businessDate === from && atIso !== null
    ? istTimeLabel(atIso)
    : rowWhen(businessDate, atIso);
}

/** One table: DOM/header order and width allocation follow the shell breakpoint. */
export function moneyLedgerColumns<Row>(columns: readonly DataTableColumn<Row>[], narrow: boolean): readonly DataTableColumn<Row>[] {
  if(columns.length===6)return columns.map(column=>({...column,width:'16.6667%'}));
  const widths: Record<string, number> = { when: 90, type: narrow ? 260 : 100, reference: 170, mode: 65, by: 105, proof: 100, amount: 145, drawer: 145, running: 145 };
  const order = narrow
    ? ['when', 'type', 'amount', 'running', 'reference', 'mode', 'by', 'proof', 'drawer']
    : ['when', 'type', 'reference', 'mode', 'by', 'proof', 'amount', 'drawer', 'running'];
  return columns.map(column => ({ ...column, width: `${widths[column.key]}px` }))
    .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
}

function MoneyViewTable(props: {
  readonly onOpenReceipt: (paymentId:number)=>void;
  readonly receiptBusy: boolean;
  readonly ws: MoneyWorkspaceWire;
  readonly refreshing: boolean;
  readonly filterLabel: string;
  readonly onOpenEntry: (entryId: number) => void;
}): JSX.Element {
  const { ws } = props;
  const narrow = useNarrowViewport();
  const line = countLine({
    rowCount: ws.rowCount,
    dayCount: ws.range.dayCount,
    filter: ws.filter,
    filterLabel: props.filterLabel,
    capped: ws.summary.collectionsCapped || ws.summary.entriesCapped,
  });

  if (props.refreshing && ws.rowCount === 0) {
    return <LoadingState label="Loading rows..." lines={3} />;
  }

  const emptyFor = (title: string, body: string) => (
    <EmptyState title={title} body={body} testId="money-view-empty" />
  );

  if (ws.view === 'day_ledger') {
    const rows = ws.rows as readonly MoneyDayLedgerRowWire[];
    return (
      <DataTable<MoneyDayLedgerRowWire>
        scrollable
        scrollLabel="Ledger: scroll horizontally to view all columns"
        testId="money-table-day_ledger"
        label="Ledger"
        stickyHeader
        countLine={line}
        rows={rows}
        rowKey={(r) => r.key}
        empty={emptyFor(
          'No money events in this range.',
          'Collections, expenses and deposits all appear here in time order.',
        )}
        columns={moneyLedgerColumns<MoneyDayLedgerRowWire>([
          {key:'when',header:'Time · IST',render:r=>ws.range.from===ws.range.to&&r.typeToken==='day_start'?'Day start':ws.range.from===ws.range.to&&r.typeToken==='day_close'?'Day close':moneyRowWhen(r.businessDate,r.atIso,ws.range.from,ws.range.to)},
          {key:'type',header:'Type',render:r=><AcBadge tone={'neutral'}>{r.typeToken==='collection'&&r.mode?`${paymentModeLabel(r.mode)} collection`:r.typeLabel}</AcBadge>},
          {key:'reference',header:'Description',render:r=><span className="ac-cell-stack"><span>{r.typeToken==='day_close'?'Day close':r.reference}</span>{r.typeToken==='day_close'?<small>{(()=>{const difference=ws.days.find(day=>day.businessDate===r.businessDate)?.differenceMinorUnits;return difference===null||difference===undefined?'Counted difference unavailable':difference===0?'Count matches':`${formatMinorUnitsInr(Math.abs(difference),2)} ${difference<0?'short':'extra'}`;})()}</small>:null}{r.enteredBy?<small>{r.enteredBy.label}</small>:r.typeToken==='collection'?<small>Attribution unavailable</small>:null}</span>},
          {key:'proof',header:'Proof',render:r=><span className="ac-cell-stack">
            {r.entryId!==null?<AcButton variant="ghost" size="sm" onClick={()=>props.onOpenEntry(r.entryId!)}>View entry</AcButton>:r.paymentId!==null?<AcButton variant="ghost" size="sm" disabled={props.receiptBusy} onClick={()=>props.onOpenReceipt(r.paymentId!)}>Open receipt</AcButton>:r.typeToken==='day_start'?'Carried':r.typeToken==='day_close'?'Counted':null}
            {r.proofState!==null?<small>{PROOF_LABELS[r.proofState]}</small>:null}
          </span>},
          {key:'amount',header:'Amount · ₹',align:'end',render:r=><span className="ac-cell-stack"><span>{r.amountMinorUnits===null?'Unknown':r.typeToken==='day_start'||r.typeToken==='day_close'||r.signedMinorUnits===0?formatMinorUnitsInr(r.amountMinorUnits,2):`${r.signedMinorUnits<0?'−':'+'} ${formatMinorUnitsInr(Math.abs(r.signedMinorUnits),2)}`}</span>{r.typeToken==='collection'&&r.signedMinorUnits===0?<small>No cash effect</small>:null}</span>},
          {key:'running',header:'Cash balance',align:'end',render:r=>r.runningCashMinorUnits===null?'Unavailable':formatMinorUnitsInr(r.runningCashMinorUnits,2)},
        ], narrow)}
      />
    );
  }

  if (ws.view === 'expenses') {
    const rows = ws.rows as readonly MoneyExpenseRowWire[];
    return (
      <DataTable<MoneyExpenseRowWire>
        scrollable
        scrollLabel="Expenses: scroll horizontally to view all columns"
        testId="money-table-expenses"
        label="Expenses"
        stickyHeader
        countLine={line}
        rows={rows}
        rowKey={(r) => r.key}
        onRowClick={(r) => props.onOpenEntry(r.entryId)}
        empty={emptyFor(
          'No expenses in this range.',
          'An expense reduces cash in hand and never reduces collected revenue.',
        )}
        columns={[
          {
            key: 'when',
            header: 'When',
            render: (r) => moneyRowWhen(r.businessDate, r.atIso, ws.range.from, ws.range.to),
          },
          { key: 'category', header: 'Category', render: (r) => r.category ? r.category.charAt(0).toUpperCase()+r.category.slice(1) : '' },
          {
            key: 'particulars',
            header: 'Particulars',
            render: (r) => r.particulars,
          },
          { key: 'paidTo', header: 'Paid to', render: (r) => r.paidTo ?? '' },
          { key: 'remark', header: 'Remark', render: (r) => r.remark ?? '' },
          {
            key: 'proof',
            header: 'Proof',
            render: (r) => (
              <AcBadge
                tone={proofTone(r.proofState) === 'positive' ? 'positive' : 'pending'}
                testId={`entry-proof-${r.entryId}`}
              >
                {PROOF_LABELS[r.proofState]}
              </AcBadge>
            ),
          },
          {
            key: 'by',
            header: 'Entered by',
            render: (r) => r.enteredBy.label,
          },
          {
            key: 'state',
            header: 'State',
            render: (r) => (
              <AcBadge tone={r.voidState === 'active' ? 'neutral' : 'info'}>
                {VOID_STATE_LABELS[r.voidState]}
              </AcBadge>
            ),
          },
          {
            key: 'amount',
            header: 'Amount',
            align: 'end',
            width: '145px',
            render: (r) => formatRs(r.amountMinorUnits),
          },
        ]}
      />
    );
  }

  if (ws.view === 'deposits') {
    const rows = ws.rows as readonly MoneyDepositRowWire[];
    return (
      <DataTable<MoneyDepositRowWire>
        scrollable
        scrollLabel="Bank deposits: scroll horizontally to view all columns"
        testId="money-table-deposits"
        label="Bank deposits"
        stickyHeader
        countLine={line}
        rows={rows}
        rowKey={(r) => r.key}
        onRowClick={(r) => props.onOpenEntry(r.entryId)}
        empty={emptyFor(
          'No bank deposits in this range.',
          'A deposit moves cash to the bank: it reduces cash in hand and never reduces collected revenue.',
        )}
        columns={[
          {
            key: 'when',
            header: 'When',
            render: (r) => moneyRowWhen(r.businessDate, r.atIso, ws.range.from, ws.range.to),
          },
          { key: 'bank', header: 'Bank', render: (r) => r.bank ?? '' },
          {
            key: 'reference',
            header: 'Reference',
            render: (r) => r.reference ?? '',
          },
          { key: 'remark', header: 'Remark', render: (r) => r.remark },
          {
            key: 'proof',
            header: 'Proof',
            render: (r) => (
              <AcBadge
                tone={proofTone(r.proofState) === 'positive' ? 'positive' : 'pending'}
              >
                {PROOF_LABELS[r.proofState]}
              </AcBadge>
            ),
          },
          { key: 'by', header: 'Entered by', render: (r) => r.enteredBy.label },
          {
            key: 'state',
            header: 'State',
            render: (r) => (
              <AcBadge tone={r.voidState === 'active' ? 'neutral' : 'info'}>
                {VOID_STATE_LABELS[r.voidState]}
              </AcBadge>
            ),
          },
          {
            key: 'amount',
            header: 'Amount',
            align: 'end',
            width: '145px',
            render: (r) => formatRs(r.amountMinorUnits),
          },
        ]}
      />
    );
  }

  const rows = ws.rows as readonly MoneyCollectionRowWire[];
  return (
    <DataTable<MoneyCollectionRowWire>
        scrollable
        scrollLabel="Collections by mode: scroll horizontally to view all columns"
      testId="money-table-collections_by_mode"
      label="Collections by mode"
      stickyHeader
      countLine={`${line} - totals by mode ride the summary band above`}
      rows={rows}
      rowKey={(r) => r.key}
      empty={emptyFor(
        'No collections in this range.',
        'Every payment method appears here; only cash reaches the drawer.',
      )}
      columns={[
        {
          key: 'when',
          header: 'When',
          render: (r) => moneyRowWhen(r.businessDate, r.atIso, ws.range.from, ws.range.to),
        },
        {
          key: 'reference',
          header: 'Reference',
          render: (r) => `Payment #${r.paymentId}`,
        },
        {
          key: 'mode',
          header: 'Mode',
          render: (r) => (
            <AcBadge tone={r.reachesDrawer ? 'positive' : 'info'}>
              {paymentModeLabel(r.method)}
            </AcBadge>
          ),
        },
        {
          key: 'drawer',
          header: 'Reaches drawer',
          render: (r) => (r.reachesDrawer ? 'Yes - cash' : 'No'),
        },
        // G1/G2: the Patient column EXISTS only when the server sent the
        // field. For every other role there is no column and no value --
        // the absence is structural, never a CSS rule.
        ...(ws.viewer.canSeeNames
          ? [
              {
                key: 'patient',
                header: 'Patient',
                render: (r: MoneyCollectionRowWire) => r.patientLabel ?? '',
              },
            ]
          : []),
        {
          key: 'amount',
          header: 'Amount',
          align: 'end' as const,
          render: (r: MoneyCollectionRowWire) => formatRs(r.amountMinorUnits),
        },
      ]}
    />
  );
}

// ---- M2: Add expense / petty cash sheet ------------------------------------

function ExpenseSheet(props: {
  readonly initialKind?: 'spent' | 'petty_cash';
  readonly day: MoneyDayWire;
  readonly currentBusinessDate: string;
  readonly clinicId: number;
  readonly clinicLabel?: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): JSX.Element {
  const { day, clinicId } = props;
  const [storageKey,setStorageKey]=useState<string|null>(null);
  const [kind, setKind] = useState<'spent' | 'petty_cash'>(
    props.initialKind ?? 'spent',
  );
  const [category, setCategory] = useState('');
  const [particulars, setParticulars] = useState('');
  const [comment,setComment]=useState('');
  const [paidTo, setPaidTo] = useState('');
  const [amountRaw, setAmountRaw] = useState('');
  const [referenceNumber, setReferenceNumber] = useState(
    '',
  );
  const [photo, setPhoto] = useState<CapturedPhoto | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [capturingPhoto, setCapturingPhoto] = useState(false);
  const [approverId, setApproverId] = useState<number | ''>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(
    null,
  );
  const [receipt, setReceipt] = useState<{
    entry: MoneyDrawerEntryWire;
    replayed: boolean;
  } | null>(null);
  const requestIdRef = useRef<string>(crypto.randomUUID());
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(()=>{
    let current=true;
    void moneyOperationSession('draft').then(session=>{
      if(!current)return;
      const legacyKey=session.key+':'+draftStorageKey(clinicId,day.businessDate);
      const legacy=parseDraft(window.sessionStorage.getItem(legacyKey));
      if(legacy){
        const destination=session.key+':'+draftStorageKey(clinicId,day.businessDate,legacy.kind);
        if(window.sessionStorage.getItem(destination)===null) window.sessionStorage.setItem(destination,serializeDraft(legacy));
        window.sessionStorage.removeItem(legacyKey);
      }
      const requestedKind=props.initialKind??'spent';
      const key=session.key+':'+draftStorageKey(clinicId,day.businessDate,requestedKind);
      const draft=parseDraft(window.sessionStorage.getItem(key));
      if(draft&&draft.kind===requestedKind){setKind(draft.kind);setCategory(draft.category);setParticulars(draft.particulars);setComment(draft.comment??'');setPaidTo(draft.paidTo);setAmountRaw(draft.amountRaw);setReferenceNumber(draft.referenceNumber);setDraftSavedAt(draft.savedAtIso);}
      setStorageKey(key);
    }).catch(e=>{if(current)setSubmitError(drawerFeedback(e, 'save'));});
    return()=>{current=false;};
  },[clinicId,day.businessDate]);

  // LES-012: autosave the draft on every change; survives sheet close.
  useEffect(() => {
    const draft: MoneySheetDraft = {
      kind,
      category,
      particulars,
      comment,
      paidTo,
      amountRaw,
      referenceNumber,
      savedAtIso: new Date().toISOString(),
    };
    if (storageKey!==null) {
      try {
        if(draftHasContent(draft)) window.sessionStorage.setItem(storageKey, serializeDraft(draft));
        else window.sessionStorage.removeItem(storageKey);
        setDraftSavedAt(draftHasContent(draft)?draft.savedAtIso:null);
      } catch {setDraftSavedAt(null);setSubmitError('This draft could not be saved in this browser. Keep the form open while you resolve browser storage.');}
    }
  }, [kind, category, particulars, comment, paidTo, amountRaw, referenceNumber, storageKey]);

  const amountMinorUnits = parseInrToMinorUnits(amountRaw);
  const needsApproval =
    photo === null && referenceNumber.trim() === '';

  const onPickPhoto = async (file: File | null) => {
    setPhotoError(null);
    setPhoto(null);
    if (!file) return;
    setCapturingPhoto(true);
    try {
      const captured = await fileToCapturedPhoto(
        file,
        day.config.proofImageMaxBytes,
        day.config.proofImageAllowedTypes,
      );
      setPhoto(captured);
    } catch (err) {
      setPhoto(null);
      setPhotoError(drawerFeedback(err, 'voucher'));
    } finally {
      setCapturingPhoto(false);
    }
  };

  const submit = async () => {
    if(storageKey===null||busy||capturingPhoto)return;
    setSubmitError(null);
    const fieldErrors = validateExpenseSheet({
      kind,
      category,
      particulars,
      paidTo,
      amountMinorUnits,
      hasPhoto: photo !== null,
      referenceNumber,
      approverPicked: approverId !== '',
      approversAvailable: day.config.approvers.length > 0,
    });
    if (photoError !== null) {
      (fieldErrors as Record<string, string>).proof = photoError;
    }
    setErrors(fieldErrors as Record<string, string>);
    if (Object.keys(fieldErrors).length > 0) return;
    setBusy(true);
    try {
      const result = await recordMoneyEntryApi({
        clinicId,
        businessDate:day.businessDate,
        kind,
        category: kind === 'spent' ? category : null,
        particulars,
        comment:comment.trim()||null,
        paidTo: paidTo.trim() !== '' ? paidTo : null,
        amountMinorUnits: amountMinorUnits as number,
        proofReferenceNumber:
          referenceNumber.trim() !== '' ? referenceNumber : null,
        proofImageBase64: photo?.base64 ?? null,
        proofImageMimeType: photo?.mimeType ?? null,
        noProofApprovedByUserRoleId:
          needsApproval && approverId !== '' ? approverId : null,
        requestId: requestIdRef.current,
      });
      if(storageKey!==null)window.sessionStorage.removeItem(storageKey);
      setReceipt({ entry: result.entry, replayed: !result.created });
      props.onSaved();
    } catch (err) {
      // Preserve the refusal category and entered values, without displaying
      // raw server/transport diagnostics or asserting an uncertain save failed.
      setSubmitError(drawerFeedback(err, 'save'));
    } finally {
      setBusy(false);
    }
  };

  const kindLabel = kind === 'spent' ? 'expense' : day.config.pettyCashLabel.toLowerCase();
  const submitLabel = busy?'Saving…':submitError!==null?`Retry saving ${kindLabel}`:`Save ${kindLabel}`;

  if(receipt!==null)return <EntryDetailsDrawer entry={receipt.entry} currentBusinessDate={props.currentBusinessDate} day={day} clinicId={clinicId} onClose={props.onClose} onChanged={props.onSaved}/>;

  return (
    <ModalShell
      trapFocus
      title={kind==='spent'?'Add expense':'Record petty cash'}
      onClose={props.onClose}
      closeDisabled={busy} closeOnScrim={!busy} closeOnEscape={!busy}
      testId="expense-sheet"
    >
      {Object.keys(errors).length > 0 ? (
        <div className="money-sheet__esum" data-testid="blocked-summary">
          {Object.keys(errors).length} field
          {Object.keys(errors).length === 1 ? '' : 's'} needed. Nothing typed is
          lost.
        </div>
      ) : null}
      {submitError !== null ? (
        <div className="money-sheet__esum" role="alert">{submitError}</div>
      ) : null}
      <p className="money-form-context">{formatIstDate(`${day.businessDate}T00:00:00+05:30`)} · {props.clinicLabel || 'Clinic name unavailable'}</p>
      <div className="money-sheet__fgrid">
        {kind === 'spent' ? (
          <div className={`money-sheet__f${errors.category ? ' money-sheet__f--err' : ''}`}>
            <label htmlFor="expense-category">
              Category <span className="money-sheet__req">Required</span>{' '}

            </label>
            <select id="expense-category" disabled={busy||storageKey===null} className="money-sheet__sel" value={category} onChange={event=>setCategory(event.target.value)} data-testid="category-select">
              <option value="">Choose</option>{day.config.expenseCategories.map(value=><option key={value} value={value}>{value.charAt(0).toUpperCase()+value.slice(1)}</option>)}
            </select>
            {errors.category ? (
              <div className="money-sheet__emsg" data-testid="category-error">
                {errors.category}
              </div>
            ) : null}
          </div>
        ) : null}
        <div
          className={`money-sheet__f money-sheet__f--wide${errors.particulars ? ' money-sheet__f--err' : ''}`}
        >
          <label htmlFor="expense-purpose">
            What was this for? <span className="money-sheet__req">Required</span>
          </label>
          <input disabled={busy||storageKey===null}
            className="money-sheet__inp"
            value={particulars}
            onChange={(e) => setParticulars(e.target.value)}
            id="expense-purpose" data-testid="particulars-input"
          />
          {errors.particulars ? (
            <div className="money-sheet__emsg">{errors.particulars}</div>
          ) : null}
        </div>
        {kind==='spent'?<div className="money-sheet__f"><label htmlFor="expense-remarks">Remarks (optional)</label><input id="expense-remarks" className="money-sheet__inp" value={comment} disabled={busy||storageKey===null} onChange={event=>setComment(event.target.value)} data-testid="expense-remarks"/></div>:null}
        {(
          <div className={`money-sheet__f${errors.paidTo ? ' money-sheet__f--err' : ''}`}>
            <label htmlFor="expense-paid-to">
              Paid to {kind==='spent'?<span className="money-sheet__req">Required</span>:'(optional)'}
            </label>
            <input disabled={busy||storageKey===null}
              id="expense-paid-to"
              className="money-sheet__inp"
              value={paidTo}
              onChange={(e) => setPaidTo(e.target.value)}
            />
            {errors.paidTo ? (
              <div className="money-sheet__emsg">{errors.paidTo}</div>
            ) : null}
          </div>
        )}
        <div className={`money-sheet__f${errors.amount ? ' money-sheet__f--err' : ''}`}>
          <label htmlFor="expense-amount">
            Amount (₹) <span className="money-sheet__req">Required</span>
          </label>
          <input disabled={busy||storageKey===null}
            className="money-sheet__inp money-sheet__num"
            value={amountRaw}
            onChange={(e) => setAmountRaw(e.target.value)}
            placeholder=""
            inputMode="decimal"
            id="expense-amount" data-testid="amount-input"
          />
          {errors.amount ? (
            <div className="money-sheet__emsg" data-testid="amount-error">
              {errors.amount}
            </div>
          ) : null}
        </div>
        <div
          className={`money-sheet__f money-sheet__f--wide${errors.proof ? ' money-sheet__f--err' : ''}`}
        >
          <label htmlFor="expense-reference">Voucher reference · offline fallback</label>
          <input id="expense-reference" className="money-sheet__inp" disabled={busy||storageKey===null} value={referenceNumber} onChange={event=>setReferenceNumber(event.target.value)} data-testid="reference-input"/>
        </div>
        <div className="money-sheet__f money-sheet__f--wide">
          <label htmlFor="expense-photo">Voucher image</label>
          <input ref={fileRef} id="expense-photo" type="file" disabled={busy||capturingPhoto||storageKey===null} accept={day.config.proofImageAllowedTypes.join(',')} onChange={event=>void onPickPhoto(event.target.files?.[0]??null)} data-testid="photo-input"/>
          {photo?<img className="money-sheet__proofimg" src={`data:${photo.mimeType};base64,${photo.base64}`} alt="Selected voucher"/>:<p className="money-form-help">Attach a voucher image. If the image is unavailable offline, record its reference number and add the photo later. A reference alone stays “No photo yet”.</p>}
          {capturingPhoto?<p role="status">Preparing voucher image…</p>:null}
          {photoError?<p role="alert">{photoError}</p>:null}
          {needsApproval&&day.config.approvers.length>0?<div className="money-sheet__f"><label htmlFor="expense-approver">OK by</label><select id="expense-approver" disabled={busy||storageKey===null} value={approverId} onChange={event=>setApproverId(event.target.value===''?'':Number(event.target.value))} data-testid="approver-select"><option value="">Choose</option>{day.config.approvers.map(person=><option key={person.id} value={person.id}>{person.label} ({person.role})</option>)}</select></div>:null}
          {errors.proof ? (
            <div className="money-sheet__emsg" data-testid="proof-error">
              {errors.proof}
            </div>
          ) : null}
        </div>
      </div>
      <div className="money-form-actions"><Button className="money-cp1-button" onClick={()=>void submit()} disabled={busy||capturingPhoto||storageKey===null} data-testid="submit-expense">{submitLabel}</Button>{draftSavedAt?<small data-testid="draft-saved">Draft saved {istTimeLabel(draftSavedAt)}</small>:null}</div>
    </ModalShell>
  );
}

// ---- M3: Close day sheet -----------------------------------------------------

function CloseDaySheet(props: {
  readonly ledgerRows: readonly MoneyDayLedgerRowWire[] | null;
  readonly onRequestRows: () => void;
  readonly day: MoneyDayWire;
  readonly clinicId: number;
  readonly onClose: () => void;
  readonly onClosed: () => void;
}): JSX.Element {
  const { clinicId } = props;
  const [refreshedDay,setRefreshedDay]=useState<MoneyDayWire|null>(null);
  const day=refreshedDay??props.day;
  const eq = day.equation;
  const [evidenceTerm,setEvidenceTerm]=useState<'start'|'cash'|'spent'|'deposits'|null>(null);
  const [countedRaw, setCountedRaw] = useState('');
  const [reasonCategory, setReasonCategory] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [doctorId, setDoctorId] = useState<number | ''>(
    day.config.doctors.length === 1 ? day.config.doctors[0]!.id : '',
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved,setSaved]=useState<SavedClose|null>(null);
  const [check,setCheck]=useState<CloseCheck|null>(null);
  const [recovering,setRecovering]=useState(true);
  const inFlight=useRef(false);
  useEffect(()=>{
    let current=true;
    void pendingClose(clinicId,day.businessDate).then(async pending=>{
      if(!current)return;
      if(pending){setSaved(pending);setCountedRaw(String(pending.intent.countedCashMinorUnits/100));
        setReasonCategory(pending.intent.differenceReasonCategory??'');setReasonText(pending.intent.differenceReasonText??'');
        setDoctorId(pending.intent.verifiedByDoctorId??'');
        if(pending.phase==='checking'){const next=await readCloseCheck(clinicId,pending.checkId);if(current)setCheck(next);}
      }
    }).catch(e=>{if(current)setSubmitError(drawerFeedback(e, 'close'));}).finally(()=>{if(current)setRecovering(false);});
    return()=>{current=false;};
  },[clinicId,day.businessDate]);

  const derived = saved?.intent.expectedDerivedCloseMinorUnits ?? eq?.cashNowMinorUnits ?? null;
  const counted = parseInrToMinorUnits(countedRaw);
  const conclusion = deriveCloseConclusion(derived??0, derived===null?null:counted);

  const submit = async () => {
    if(inFlight.current || recovering)return;
    setSubmitError(null);
    const fieldErrors:Record<string,string>={};
    if(counted===null)fieldErrors.counted='Type the counted cash';
    if(derived===null)fieldErrors.counted='The day figure is unavailable. Reload before counting.';
    if(conclusion.kind==='difference'){
      if(!reasonCategory)fieldErrors.reasonCategory='Pick why';
      if(!reasonText.trim())fieldErrors.reasonText='Your own words';
    }
    if(day.config.verifierRequired&&doctorId==='')fieldErrors.verifier='Pick the second check';
    setErrors(fieldErrors);if(Object.keys(fieldErrors).length)return;
    inFlight.current=true;setBusy(true);
    try {
      if(!saved || saved.phase==='requesting') {
        const result=await requestCloseCheck(saved?.intent??{
          clinicId,businessDate:day.businessDate,countedCashMinorUnits:counted!,expectedDerivedCloseMinorUnits:derived!,
          differenceReasonCategory:conclusion.kind==='difference'?reasonCategory:null,
          differenceReasonText:conclusion.kind==='difference'?reasonText.trim():null,
          verifiedByDoctorId:day.config.verifierRequired&&doctorId!==''?doctorId:null,
        });
        setSaved(result.saved);setCheck(await readCloseCheck(clinicId,result.saved.checkId));
      } else if(saved.phase==='closing' || (check && !check.sentBack && !check.sourceChanged && (check.confirmed || !day.config.verifierRequired))) {
        await finishClose(saved);props.onClosed();
      } else {
        setCheck(await readCloseCheck(clinicId,saved.checkId));
      }
    }catch(e){
      setSubmitError(drawerFeedback(e, 'close'));
      const pending=await pendingClose(clinicId,day.businessDate).catch(()=>saved);
      setSaved(pending);
    }finally{inFlight.current=false;setBusy(false);}
  };
  const changeCount=async()=>{
    if(inFlight.current)return;inFlight.current=true;setBusy(true);
    try{
      const session=await moneyOperationSession('close');
      const fresh=await fetchMoneyDay(clinicId,day.businessDate);await session.assertCurrent();
      if(fresh.clinicId!==clinicId||fresh.businessDate!==day.businessDate||fresh.equation===null)throw new Error('Day source unavailable');
      await changeSavedCount(clinicId,day.businessDate);await session.assertCurrent();
      setRefreshedDay(fresh);setSaved(null);setCheck(null);setSubmitError(null);
    }
    catch(e){setSubmitError(drawerFeedback(e, 'close'));}finally{inFlight.current=false;setBusy(false);}
  };
  const frozen=busy||recovering||saved!==null;
  const ready=saved!==null&&saved.phase==='checking'&&check!==null&&!check.sentBack&&!check.sourceChanged&&(check.confirmed||!day.config.verifierRequired);
  const closeLabel = counted !== null && derived !== null && counted !== derived ? `Close ${formatMinorUnitsInr(Math.abs(counted-derived), 2)} ${counted < derived ? 'short' : 'extra'}` : 'Close day';
  const submitLabel=recovering?'Checking saved close…':saved?.phase==='closing'?'Recover saved close':!saved||saved.phase==='requesting'?'Request second check':ready?closeLabel:'Refresh check status';

  const waitingDoctor = day.config.doctors.find(d=>d.id===saved?.intent.verifiedByDoctorId)?.name ?? 'the assigned doctor';
  const checkStatus = saved?.phase==='checking'?<div role="status" data-testid="close-check-status">
        {check?<p data-testid="close-check-requester">Requested by {check.counterName?.trim()||'staff name not recorded'} · {check.context.counterRole==='receptionist'?'Reception':check.context.counterRole||'role not recorded'} · {check.requestedAt?formatTimestampIst(check.requestedAt):'request time not recorded'}</p>:<p data-testid="close-check-requester">Requester details unavailable. Refresh check status.</p>}
        <p>{check?.sourceChanged?'The day changed. Count again and request a new check.':check?.sentBack?`Sent back for recount.${check.review?.note?' '+check.review.note:''}`:check?.confirmed?'Second check confirmed.':day.config.verifierRequired?`Waiting for ${waitingDoctor} to confirm this count in their own session.`:'No second checker is required by this branch setting.'}</p>
        {day.config.verifierRequired&&!check?.confirmed&&!check?.sentBack?<p><a href={`/dashboard/money-check?clinicId=${clinicId}&checkId=${encodeURIComponent(saved.checkId)}`}>Open second check</a> · The assigned doctor can also open this from Cash check waiting on their Home.</p>:null}
        <Button className="money-cp1-button" variant="outline" disabled={busy} onClick={()=>void changeCount()}>Change count and request a new check</Button>
      </div>:null;

  return (
    <ModalShell trapFocus closeDisabled={busy} closeOnScrim={!busy} closeOnEscape={!busy}
      title="Count and close day"
      onClose={busy?()=>{}:props.onClose}
      testId="close-sheet"

    >
      {submitError !== null ? (
        <div role="alert" className="money-sheet__esum">{submitError}</div>
      ) : null}
      <p>Any expense to report? Record it before closing.</p>
      {eq !== null && day.dayStart !== null ? <div data-testid="close-recap">
        <DataTable label="The day equation" testId="close-equation" rows={[
          {key:'start',label:'Day start',value:day.dayStart.minorUnits},
          {key:'cash',label:'+ Cash in',value:eq.cashInMinorUnits},
          {key:'spent',label:'− Spent',value:eq.spentMinorUnits},
          {key:'deposits',label:'− Deposited',value:eq.sentMinorUnits},
        ] as const} rowKey={row=>row.key} columns={[
          {key:'term',header:'Cash calculation',render:row=>row.label},
          {key:'value',header:'Amount',align:'end',render:row=>formatMinorUnitsInr(row.value, 2)},
          {key:'rows',header:'Evidence',align:'end',render:row=><Button className="money-cp1-button" variant="ghost" onClick={()=>{setEvidenceTerm(row.key);props.onRequestRows();}}>Rows</Button>},
        ]}/>
        <div className="money-close-expected"><strong>Expected cash</strong><strong data-testid="should-be">{derived===null?'Unavailable':formatMinorUnitsInr(derived, 2)}</strong></div>
      </div>:null}
      {evidenceTerm!==null?<section className="money-close-evidence" aria-label="Count source rows">
        <Button className="money-cp1-button" variant="ghost" onClick={()=>setEvidenceTerm(null)}>Back to count</Button>
        {props.ledgerRows===null?<p>Loading day ledger…</p>:<DataTable label="Count source rows" rows={props.ledgerRows.filter(row=>evidenceTerm==='start'?row.typeToken==='day_start':evidenceTerm==='cash'?row.typeToken==='collection'&&row.mode==='cash':evidenceTerm==='deposits'?row.typeToken==='deposit'||row.typeToken==='reversal':row.typeToken==='expense'||row.typeToken==='petty_cash'||row.typeToken==='reversal')} rowKey={row=>row.key} columns={[
          {key:'type',header:'Type',render:row=>row.typeLabel},
          {key:'what',header:'What',render:row=>row.reference},
          {key:'amount',header:'Amount',align:'end',render:row=>row.amountMinorUnits===null?'Unavailable':formatMinorUnitsInr(row.amountMinorUnits, 2)},
        ]}/>}
        <p>Saved source rows. Reversals retain their original entry reference; inspect the day ledger for their cash effect.</p>
      </section>:null}
      <div className={`money-sheet__f${errors.counted ? ' money-sheet__f--err' : ''}`}>
        <label htmlFor="money-counted-cash">
          Counted cash (₹) <span className="money-sheet__req">Required</span>
        </label>
        <div className="money-sheet__countrow">
          <input
            className="money-sheet__inp money-sheet__num"
            disabled={frozen} value={countedRaw}
            onChange={(e) => setCountedRaw(e.target.value)}
            inputMode="decimal"
            id="money-counted-cash" data-testid="counted-input"
          />
          <output className={`money-close-count-result${conclusion.kind === 'difference' ? ' money-close-count-result--difference' : ''}`} data-testid="counted-chip">
            {derived === null ? 'Reload saved payment totals before checking this count.' : counted === null ? 'Enter the physical count.' : conclusion.kind === 'match' ? 'Count matches.' : `${formatMinorUnitsInr(Math.abs(counted-derived), 2)} ${counted < derived ? 'short' : 'extra'} · Explain the difference.`}
          </output>
        </div>
        {errors.counted ? (
          <div className="money-sheet__emsg">{errors.counted}</div>
        ) : null}
      </div>
      {conclusion.kind === 'difference' ? (
        <>
          <div
            className={`money-sheet__f${errors.reasonCategory ? ' money-sheet__f--err' : ''}`}
          >
            <label htmlFor="money-close-reason">
              Difference reason <span className="money-sheet__req">Required</span>{' '}

            </label>
            <select
              className="money-sheet__sel"
              disabled={frozen} value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value)}
              id="money-close-reason" data-testid="reason-select"
            >
              <option value="">Choose</option>
              {day.config.differenceReasonCategories.map((c) => (
                <option key={c} value={c}>
                  {differenceReasonLabel(c)}
                </option>
              ))}
            </select>
            {errors.reasonCategory ? (
              <div className="money-sheet__emsg">{errors.reasonCategory}</div>
            ) : null}
          </div>
          <div
            className={`money-sheet__f${errors.reasonText ? ' money-sheet__f--err' : ''}`}
          >
            <label htmlFor="money-close-explanation">
              Explain the difference <span className="money-sheet__req">Required</span>
            </label>
            <input
              className="money-sheet__inp"
              disabled={frozen} value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              id="money-close-explanation" data-testid="reason-text"
            />
            {errors.reasonText ? (
              <div className="money-sheet__emsg">{errors.reasonText}</div>
            ) : null}
          </div>
        </>
      ) : null}
      {day.config.verifierRequired ? (
        <div
          className={`money-sheet__f money-sheet__verify${errors.verifier ? ' money-sheet__f--err' : ''}`}
        >
          <label htmlFor="money-close-checker">
            Second check
          </label>
          <select
            className="money-sheet__sel"
            disabled={frozen} value={doctorId}
            onChange={(e) =>
              setDoctorId(e.target.value === '' ? '' : Number(e.target.value))
            }
            id="money-close-checker" data-testid="verifier-select"
          >
            <option value="">Choose</option>
            {day.config.doctors.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <p>A different eligible person must check this count.</p>
          {checkStatus}
          {!ready && saved?.phase !== 'closing' ? <Button className="money-cp1-button" variant="ghost" onClick={()=>void submit()} disabled={busy||recovering||derived===null||check?.sourceChanged===true} data-testid="close-day-submit">{submitLabel}</Button> : null}
          {errors.verifier ? (
            <div className="money-sheet__emsg">{errors.verifier}</div>
          ) : null}
        </div>
      ) : null}
      {!day.config.verifierRequired ? checkStatus : null}
      <p className={`money-close-carry${conclusion.kind === 'difference' ? ' money-close-carry--difference' : ''}`} data-testid="close-conclusion">{counted === null ? 'The counted close will become the next day’s opening cash.' : `${derived !== null && counted !== derived ? `${formatMinorUnitsInr(Math.abs(counted-derived), 2)} ${counted < derived ? 'short' : 'extra'}. ` : ''}Closes at ${formatMinorUnitsInr(counted, 2)}. The next day starts with the same counted cash.`}</p>
      <p className="money-close-help">After {day.config.autoCloseTime} IST, acknowledgement is still required.</p>
      <Button className="money-cp1-button" onClick={() => void submit()}
        disabled={busy || recovering || derived === null || check?.sourceChanged === true || (day.config.verifierRequired && !ready && saved?.phase !== 'closing')}
        data-testid={ready || saved?.phase === 'closing' || !day.config.verifierRequired ? 'close-day-submit' : 'close-day-waiting'}>{ready || saved?.phase === 'closing' || !day.config.verifierRequired ? submitLabel : closeLabel}</Button>
    </ModalShell>
  );
}

// ---- First count sheet (the labeled day-1 exception) ------------------------

function FirstCountSheet(props: {
  readonly clinicId: number;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): JSX.Element {
  const [countedRaw, setCountedRaw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const requestIdRef = useRef<string>(crypto.randomUUID());
  const counted = parseInrToMinorUnits(countedRaw);
  const [pending,setPending]=useState(false);
  const identity=useConsultationIdentity();
  const scopeKey=JSON.stringify([identity,props.clinicId]);
  const currentScope=useRef(scopeKey);currentScope.current=scopeKey;
  const mounted=useRef(true);
  const [boundScope,setBoundScope]=useState<string|null>(null);
  useEffect(()=>{let active=true;mounted.current=true;setBoundScope(null);setPending(false);setCountedRaw('');setError(null);setBusy(false);
    void pendingFirstCount().then(p=>{if(!active)return;if(p&&p.clinicId!==props.clinicId)throw new Error('Open the original branch to recover its first count.');
      if(p){setCountedRaw(String(p.countedCashMinorUnits/100));requestIdRef.current=p.requestId;setPending(true);}
      else requestIdRef.current=crypto.randomUUID();setBoundScope(scopeKey);
    }).catch(e=>{if(active)setError(apiErrorMessage(e));});
    return()=>{active=false;mounted.current=false;};
  },[scopeKey,props.clinicId]);

  const submit = async () => {
    if (busy||boundScope!==scopeKey) return;
    const capturedScope=scopeKey;
    if (counted === null) {
      setError('Enter the counted cash');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await postFirstCount({
        clinicId: props.clinicId,
        countedCashMinorUnits: counted,
        requestId: requestIdRef.current,
      });
      if(mounted.current&&currentScope.current===capturedScope)props.onSaved();
    } catch (err) {
      if(mounted.current&&currentScope.current===capturedScope){
        setError(apiErrorMessage(err));
        let hasPending=true;
        try{hasPending=(await pendingFirstCount())!==null;}catch{/* Preserve the saved request when its state cannot be read. */}
        if(mounted.current&&currentScope.current===capturedScope)setPending(hasPending);
      }
    } finally {
      if(mounted.current&&currentScope.current===capturedScope)setBusy(false);
    }
  };

  return (
    <ModalShell
      title="First count"
      onClose={props.onClose}
      testId="first-count-sheet"
      footer={
        <div className="money-sheet__sheetfoot">
          <span className="money-sheet__stripnote">
            One time only. After this, openings are carried - never typed.
          </span>
          <span className="money-sheet__spacer" />
          <AcButton variant="secondary" onClick={props.onClose} disabled={busy}>
            Cancel
          </AcButton>
          <AcButton
            variant="primary"
            onClick={() => void submit()}
            disabled={busy||boundScope!==scopeKey}
            data-testid="first-count-submit"
          >
            {counted !== null ? `First count ${formatRs(counted)}` : 'First count'}
          </AcButton>
        </div>
      }
    >
      <div className={`money-sheet__f${error ? ' money-sheet__f--err' : ''}`}>
        <label>
          Counted cash <span className="money-sheet__req">Required</span>
        </label>
        <input
          className="money-sheet__inp money-sheet__num"
          disabled={busy||pending||boundScope!==scopeKey}
          value={countedRaw}
          onChange={(e) => setCountedRaw(e.target.value)}
          inputMode="decimal"
          data-testid="first-count-input"
        />
        {error ? <div className="money-sheet__emsg">{error}</div> : null}
      </div>
    </ModalShell>
  );
}

// ---- Entry details drawer (proof photo + reverse + attach) ------------------

function EntryDetailsDrawer(props: {
  readonly entry: MoneyDrawerEntryWire;
  readonly day: MoneyDayWire;
  readonly currentBusinessDate: string;
  readonly clinicId: number;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): JSX.Element {
  const { entry, day, clinicId } = props;
  const active = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const [image, setImage] = useState<{ mimeType: string; dataBase64: string } | null>(
    null,
  );
  const [imageError, setImageError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState<CapturedPhoto | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [imageAttempt, setImageAttempt] = useState(0);
  const requestIdRef = useRef<string>(crypto.randomUUID());
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setImageError(null);
    if (entry.proofImageId !== null) {
      fetchMoneyEntryProofImage(entry.id, clinicId)
        .then((img) => {
          if (!cancelled) setImage({ mimeType: img.mimeType, dataBase64: img.dataBase64 });
        })
        .catch((err) => {
          if (!cancelled) setImageError(drawerFeedback(err, 'read'));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [entry.id, entry.proofImageId, clinicId, imageAttempt]);

  const attach = async (file: File | null) => {
    if ((!file && !pendingPhoto) || busy || !active.current) return;
    setBusy(true);
    setActionError(null);
    try {
      const session = await moneyOperationSession('drawer');
      if (!active.current) return;
      const captured = file ? await fileToCapturedPhoto(
        file,
        day.config.proofImageMaxBytes,
        day.config.proofImageAllowedTypes,
      ) : pendingPhoto!;
      if (active.current) setPendingPhoto(captured);
      await session.assertCurrent();
      if (!active.current) return;
      await attachMoneyEntryProofApi(entry.id, {
        clinicId,
        imageBase64: captured.base64,
        imageMimeType: captured.mimeType,
      });
      if (!active.current) return;
      props.onChanged();
      props.onClose();
    } catch (err) {
      if (active.current) setActionError(drawerFeedback(err, 'voucher'));
    } finally {
      if (active.current) setBusy(false);
    }
  };

  const reverse = async () => {
    if (busy || !active.current || !confirmed) return;
    setBusy(true);
    setActionError(null);
    try {
      await reverseMoneyEntryApi(entry.id, {
        clinicId,
        requestId: requestIdRef.current,
      });
      if (!active.current) return;
      props.onChanged();
      props.onClose();
    } catch (err) {
      if (active.current) setActionError(drawerFeedback(err, 'reverse'));
    } finally {
      if (active.current) setBusy(false);
    }
  };

  if (confirming) return (
    <ModalShell trapFocus title="Reverse entry" testId="reverse-entry-dialog" onClose={()=>{if(!busy){setConfirming(false);setConfirmed(false);}}} closeDisabled={busy} closeOnScrim={!busy} closeOnEscape={!busy}>
      <p>ACC-E-{entry.id} · {formatMinorUnitsInr(entry.amountMinorUnits, 2)}. The original entry stays; a separate reversing entry posts on {formatIstDate(`${props.currentBusinessDate}T00:00:00+05:30`)}. The original day's recorded amounts{entry.businessDate && entry.businessDate !== props.currentBusinessDate ? ` (${formatIstDate(`${entry.businessDate}T00:00:00+05:30`)})` : ''} remain unchanged.</p>
      {actionError?<p className="money-sheet__esum" role="alert">{actionError}</p>:null}
      <label><Checkbox checked={confirmed} disabled={busy} onCheckedChange={checked=>setConfirmed(checked)}/> Confirm reversal of this entry</label>
      <div className="money-form-actions"><Button className="money-cp1-button" disabled={busy||!confirmed} onClick={()=>void reverse()}>Confirm reversal</Button></div>
    </ModalShell>
  );

  return (
    <ModalShell
      trapFocus
      title="Entry details"
      closeDisabled={busy} closeOnScrim={!busy} closeOnEscape={!busy}
      onClose={props.onClose}
      testId="entry-details"
    >
      {actionError !== null ? (
        <div className="money-sheet__esum" role="alert">{actionError}</div>
      ) : null}
      <div className="money-sheet__receiptgrid">
        <span className="money-sheet__k">Entry</span><span>ACC-E-{entry.id}</span>
        <span className="money-sheet__k">Amount</span><span>{formatMinorUnitsInr(entry.amountMinorUnits, 2)}</span>
        <span className="money-sheet__k">What</span><span>{entry.particulars}</span>
        {entry.kind==='spent'?<><span className="money-sheet__k">Remarks</span><span>{entry.comment || 'Not recorded'}</span></>:null}
        <span className="money-sheet__k">Paid to / bank</span><span>{entry.paidTo || 'Not recorded'}</span>
        <span className="money-sheet__k">Voucher number</span><span>{entry.proofReferenceNumber || 'Not recorded'}</span>
        <span className="money-sheet__k">Business date</span><span>{entry.businessDate ? formatIstDate(`${entry.businessDate}T00:00:00+05:30`) : 'Not recorded'}</span>
        {entry.reversesEntryId != null ? <><span className="money-sheet__k">Corrects entry</span><span>ACC-E-{entry.reversesEntryId}</span></> : null}
        {entry.reversedByEntryId != null ? <><span className="money-sheet__k">Reversed by</span><span>ACC-E-{entry.reversedByEntryId}</span></> : null}
        {entry.noProofApprovedRole?<><span className="money-sheet__k">No-proof approval</span><span>{entry.noProofApprovedRole}</span></>:null}
        <span className="money-sheet__k">By</span><span>{entry.recordedByLabel?.trim() || `${entry.recordedByRole || 'Unavailable'} · name unavailable`}</span>
        <span className="money-sheet__k">Time</span><span>{entry.recordedAt ? formatTimestampIst(entry.recordedAt) : 'Not recorded'}</span>
        <span className="money-sheet__k">State</span><span>{entry.reversedByEntryId != null ? 'Reversed · original retained' : entry.kind==='reversal' ? 'Reversal' : 'Recorded'}</span>
      </div>
      {entry.kind==='bank_deposit'?<p>Bank deposit reduces cash in hand. Collected revenue and expenses are unchanged.</p>:null}
      {entry.proofImageId !== null ? (
        image !== null ? (
          <img
            className="money-sheet__proofimg"
            alt="Voucher"
            src={`data:${image.mimeType};base64,${image.dataBase64}`}
          />
        ) : imageError !== null ? (
          <><p className="money-sheet__esum" role="alert">{imageError}</p><Button className="money-cp1-button" onClick={()=>setImageAttempt(value=>value+1)}>Retry loading voucher</Button></>
        ) : (
          <LoadingState label="Loading photo..." lines={2} />
        )
      ) : entry.kind !== 'reversal' ? (
        <div className="money-sheet__proof">
          <p className="money-sheet__prooftx">No photo yet</p>
          {day.viewer.canWrite ? (
            <>
              <label htmlFor="entry-voucher">Attach a voucher</label>
              <input
                ref={fileRef}
                id="entry-voucher"
                disabled={busy || pendingPhoto !== null}
                type="file"
                accept={day.config.proofImageAllowedTypes.join(',')}
                onChange={(e) => void attach(e.target.files?.[0] ?? null)}
              />
            </>
          ) : null}
          {pendingPhoto?<p role="status">Save not confirmed. Your selected voucher is kept for retry.</p>:null}
          {pendingPhoto?<img className="money-sheet__proofimg" alt="Selected voucher" src={`data:${pendingPhoto.mimeType};base64,${pendingPhoto.base64}`}/>:null}
          {pendingPhoto?<Button className="money-cp1-button" disabled={busy} onClick={()=>void attach(null)}>Retry saving voucher</Button>:null}
        </div>
      ) : null}
      {day.viewer.canWrite && entry.kind !== 'reversal' && entry.reversedByEntryId === null ? <Button variant="ghost" className="money-cp1-button money-cp1-button--quiet" disabled={busy} data-testid="reverse-entry" onClick={()=>{setActionError(null);setConfirming(true);}}>Reverse entry</Button> : null}
    </ModalShell>
  );
}

// ---- C3/R5: Record bank deposit sheet ---------------------------------------
//
// A deposit is a drawer OUTFLOW that is NOT an expense (order B2): it
// reduces cash in hand and never touches collected revenue. It carries no
// expense category -- the destination bank rides "Bank", the transaction
// id rides "Reference", the remark rides "What this was". Proof follows
// the SAME FD-3/FD-4 lifecycle as an expense: photo, or reference, or the
// configured approval; no second proof rule is minted.

function DepositSheet(props: {
  readonly day: MoneyDayWire;
  readonly currentBusinessDate: string;
  readonly clinicId: number;
  readonly clinicLabel?: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): JSX.Element {
  const { day, clinicId } = props;
  const [amountRaw, setAmountRaw] = useState('');
  const [remark, setRemark] = useState('');
  const [bank, setBank] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [approverId, setApproverId] = useState<number | ''>('');
  const [photo, setPhoto] = useState<CapturedPhoto | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [capturingPhoto, setCapturingPhoto] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [saved, setSaved] = useState<MoneyDrawerEntryWire | null>(null);
  // Idempotent submit (B1): the SAME token rides every retry of this
  // sheet, so a double-click or a flaky network can never mint a second
  // deposit -- the server replays the committed row instead.
  const requestIdRef = useRef<string>(crypto.randomUUID());

  const amountMinorUnits = parseInrToMinorUnits(amountRaw);
  const approversAvailable = day.config.approvers.length > 0;

  // Clear obsolete field feedback as the operator repairs the form; submit
  // still validates the entire current payload before any write.
  useEffect(() => {
    setErrors(previous => {
      if (Object.keys(previous).length === 0) return previous;
      const next: Record<string,string> = {...validateDepositSheet({amountMinorUnits,remark,hasPhoto:photo!==null,referenceNumber,approverPicked:approverId!=='',approversAvailable})};
      if(photoError!==null)next.proof=photoError;
      if(!(day.config.depositBanks??[]).includes(bank))next.bank='Choose a configured bank';
      return next;
    });
  },[amountMinorUnits,remark,photo,referenceNumber,approverId,approversAvailable,photoError,day.config.depositBanks,bank]);

  const submit = async () => {
    if (busy || capturingPhoto) return;
    setSubmitError(null);
    const fieldErrors = validateDepositSheet({
      amountMinorUnits,
      remark,
      hasPhoto: photo !== null,
      referenceNumber,
      approverPicked: approverId !== '',
      approversAvailable,
    });
    if(photoError!==null)(fieldErrors as Record<string,string>).proof=photoError;
    if(!(day.config.depositBanks??[]).includes(bank))(fieldErrors as Record<string,string>).bank='Choose a configured bank';
    setErrors(fieldErrors as Record<string, string>);
    if (Object.keys(fieldErrors).length > 0) return;
    setBusy(true);
    try {
      const result = await recordMoneyEntryApi({
        clinicId,
        businessDate:day.businessDate,
        kind: 'bank_deposit',
        category: null,
        particulars: remark.trim(),
        paidTo: bank.trim() === '' ? null : bank.trim(),
        amountMinorUnits: amountMinorUnits as number,
        proofReferenceNumber:
          referenceNumber.trim() === '' ? null : referenceNumber.trim(),
        proofImageBase64: photo?.base64 ?? null,
        proofImageMimeType: photo?.mimeType ?? null,
        noProofApprovedByUserRoleId: approverId === '' ? null : approverId,
        requestId: requestIdRef.current,
      });
      setSaved(result.entry);
      props.onSaved();
    } catch (err) {
      setRetrying(true);setSubmitError(drawerFeedback(err, 'deposit'));
    } finally {
      setBusy(false);
    }
  };

  if (saved !== null) return <EntryDetailsDrawer entry={saved} currentBusinessDate={props.currentBusinessDate} day={day} clinicId={clinicId} onClose={props.onClose} onChanged={props.onSaved}/>;
  if (!(day.config.depositBanks??[]).length) return <ModalShell trapFocus title="Record bank deposit" testId="deposit-sheet" onClose={props.onClose}><p>No deposit bank is configured for this organization.</p></ModalShell>;

  return (
    <ModalShell trapFocus
      title="Record bank deposit"
      onClose={props.onClose}
      closeDisabled={busy} closeOnScrim={!busy} closeOnEscape={!busy}
      testId="deposit-sheet"
    >
      {submitError !== null ? (
        <p role="alert" className="money-sheet__esum">{submitError}</p>
      ) : null}
      <p className="money-form-context">{formatIstDate(`${day.businessDate}T00:00:00+05:30`)} · {props.clinicLabel || 'Clinic name unavailable'}</p>
      <div className="money-sheet__grid">
        <div
          className={`money-sheet__f money-sheet__f--wide${errors.remark ? ' money-sheet__f--err' : ''}`}
        >
          <label htmlFor="deposit-remark">
            What was this for? <span className="money-sheet__req">Required</span>
          </label>
          <input
            id="deposit-remark" disabled={busy}
            className="money-sheet__inp"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            data-testid="deposit-remark"
          />
          {errors.remark ? (
            <div className="money-sheet__emsg">{errors.remark}</div>
          ) : null}
        </div>
        <div className="money-sheet__f">
          <label htmlFor="deposit-bank">Bank destination</label>
          <select id="deposit-bank" disabled={busy} className="money-sheet__sel" value={bank} onChange={e=>setBank(e.target.value)} data-testid="deposit-bank">
            <option value="">Choose a bank</option>
            {(day.config.depositBanks??[]).map(name=><option key={name} value={name}>{name}</option>)}
          </select>
          {(day.config.depositBanks??[]).length===0?<p>No deposit bank is configured for this organization.</p>:null}
          {errors.bank?<div className="money-sheet__emsg">{errors.bank}</div>:null}
        </div>

        <div
          className={`money-sheet__f${errors.amount ? ' money-sheet__f--err' : ''}`}
        >
          <label htmlFor="deposit-amount">
            Amount (₹) <span className="money-sheet__req">Required</span>
          </label>
          <input
            id="deposit-amount" disabled={busy}
            className="money-sheet__inp"
            value={amountRaw}
            onChange={(e) => setAmountRaw(e.target.value)}
            inputMode="decimal"
            data-testid="deposit-amount"
          />
          {errors.amount ? (
            <div className="money-sheet__emsg">{errors.amount}</div>
          ) : null}
        </div>

      </div>

      <div
        className={`money-sheet__proof${errors.proof ? ' money-sheet__f--err' : ''}`}
      >
        <div className="money-sheet__f">
          <label htmlFor="deposit-reference">Bank receipt reference · offline fallback</label>
          <input
            id="deposit-reference" disabled={busy}
            className="money-sheet__inp"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            data-testid="deposit-reference"
          />
        </div>
        <label htmlFor="deposit-photo">Deposit slip</label>
        <input
          id="deposit-photo"
          type="file"
          disabled={busy||capturingPhoto}
          accept={day.config.proofImageAllowedTypes.join(',')}
          onChange={(e) => {
            const file = e.target.files?.[0];
            setPhotoError(null);
            setPhoto(null);
            if (!file) return;
            setCapturingPhoto(true);
            void fileToCapturedPhoto(
              file,
              day.config.proofImageMaxBytes,
              day.config.proofImageAllowedTypes,
            )
              .then(setPhoto)
              .catch((err: unknown) => setPhotoError(apiErrorMessage(err)))
              .finally(()=>setCapturingPhoto(false));
          }}
          data-testid="deposit-photo"
        />
        {photo !== null ? (
          <img className="money-sheet__proofimg" src={`data:${photo.mimeType};base64,${photo.base64}`} alt="Selected bank receipt"/>
        ) : null}
        {capturingPhoto?<p role="status">Preparing bank receipt image…</p>:null}
        {photoError !== null ? (
          <div className="money-sheet__emsg">{photoError}</div>
        ) : null}
        {approversAvailable && photo === null && referenceNumber.trim() === '' ? (
          <div className="money-sheet__f">
            <label htmlFor="deposit-approver">Saved without proof - OK by</label>
            <select
              id="deposit-approver" disabled={busy}
              className="money-sheet__inp"
              value={approverId}
              onChange={(e) =>
                setApproverId(e.target.value === '' ? '' : Number(e.target.value))
              }
              data-testid="deposit-approver"
            >
              <option value="">Not needed</option>
              {day.config.approvers.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {errors.proof ? (
          <div className="money-sheet__emsg" data-testid="deposit-proof-error">
            {errors.proof}
          </div>
        ) : null}
      </div>
      {photo===null?<p className="money-form-help">Attach a voucher image. If the image is unavailable offline, record its reference number and add the photo later. A reference alone stays “No photo yet”.</p>:null}
      <div className="money-form-actions"><Button className="money-cp1-button" onClick={()=>void submit()} disabled={busy||capturingPhoto} data-testid="deposit-save">{busy?'Saving…':retrying?'Retry saving deposit':'Save deposit'}</Button></div>
    </ModalShell>
  );
}
